import { API_CONFIG } from '../lib/apiConfig';
import { parseCsv } from '../lib/csv';
import { createCsvStatsHook } from '../lib/hookFactories';
import { SdBin, SdStats, SdYearStats } from '../lib/types';
import { SD_PALETTE } from '../lib/sdColors';

// CSV schema (wide; one row per snapshot year):
//   year,non_forest_acres,mean_rd,acres_0_to_30,acres_30_to_60,acres_60_to_80,acres_80_plus
//   2016,12345.67,0.55,1100.0,5800.0,2500.0,950.0
//
// non_forest_acres is shed area without an RD value (water, developed, bare,
// ag, nodata) and is constant across years; we accept the per-row redundancy
// for self-contained rows. mean_rd is the area-weighted mean across forested
// pixels in 0–1 RD space. Bin acres are forested-pixel acres in each RD class.
const ACRE_COLS: Record<SdBin, string> = {
  '0_to_30':  'acres_0_to_30',
  '30_to_60': 'acres_30_to_60',
  '60_to_80': 'acres_60_to_80',
  '80_plus':  'acres_80_plus',
};

const parseSdStats = (text: string): SdStats | null => {
  const rows = parseCsv(text);
  const yearStats: SdYearStats[] = [];

  rows.forEach(row => {
    const year = Number(row['year']);
    if (!Number.isFinite(year)) return;

    const binAcres = SD_PALETTE.order.reduce((acc, bin) => {
      const v = Number(row[ACRE_COLS[bin]]);
      acc[bin] = Number.isFinite(v) ? v : 0;
      return acc;
    }, {} as Record<SdBin, number>);

    const forestAcres = SD_PALETTE.order.reduce((s, bin) => s + binAcres[bin], 0);
    const nonForestAcres = Number(row['non_forest_acres']);
    const meanRd = Number(row['mean_rd']);

    yearStats.push({
      year,
      meanRd: Number.isFinite(meanRd) ? meanRd : 0,
      binAcres,
      forestAcres,
      nonForestAcres: Number.isFinite(nonForestAcres) ? nonForestAcres : 0,
    });
  });

  if (yearStats.length === 0) return null;
  yearStats.sort((a, b) => a.year - b.year);

  return {
    years: yearStats,
    latest: yearStats[yearStats.length - 1],
  };
};

/**
 * Fetches Stand Density (RD) per-year zonal stats for the supply shed defined
 * by (millCode, driveTime). CSV lives at
 * sfi/stand_density/{mill}_{dt}min_stand_density.csv.
 */
export const useStandDensity = createCsvStatsHook({
  path: API_CONFIG.s3.paths.sdStats,
  parse: parseSdStats,
  errorPrefix: 'Failed to load stand density data',
});
